import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from '$lib/locales/en.json';
import da from '$lib/locales/da.json';

// i18n setup. Two languages, flat JSON catalogs imported synchronously so the
// first paint is already translated (no Suspense boundary needed for t()).
//
// Persistence: locale lives in localStorage under `rentallens.locale`,
// matching the `rentallens.theme` per-device pattern. Browser default is
// honoured only on the very first visit (no key in localStorage).
//
// Tests: NODE_ENV === 'test' pins lng to 'en' so existing assertions on
// English copy keep passing without churn.

export type Locale = 'en' | 'da';

export const SUPPORTED_LOCALES: Locale[] = ['en', 'da'];
export const STORAGE_KEY = 'rentallens.locale';
export const DEFAULT_LOCALE: Locale = 'en';

// BCP47 tag used for Intl formatters. Kept separate from i18next's lng so we
// can map e.g. 'en' → 'en-DK' if we ever want English copy with Danish
// number formatting. Today, English UI uses 'en-DK' to keep the kr-symbol and
// thousands grouping that analysts already rely on.
const INTL_TAG: Record<Locale, string> = {
  en: 'en-DK',
  da: 'da-DK'
};

function readStored(): Locale | null {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'en' || v === 'da' ? v : null;
}

function browserDefault(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const langs = (navigator.languages && navigator.languages.length > 0)
    ? navigator.languages
    : [navigator.language ?? ''];
  for (const raw of langs) {
    const tag = (raw || '').toLowerCase();
    if (tag.startsWith('da')) return 'da';
    if (tag.startsWith('en')) return 'en';
  }
  return DEFAULT_LOCALE;
}

function pickInitialLocale(): Locale {
  // Tests: pin English so existing assertions remain readable. The
  // language-switcher tests can still call setLocale('da') to flip.
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
    return 'en';
  }
  return readStored() ?? browserDefault();
}

const initialLocale = pickInitialLocale();

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    da: { translation: da }
  },
  lng: initialLocale,
  fallbackLng: 'en',
  interpolation: {
    // React already escapes; double-escaping breaks copy like "kr/m²".
    escapeValue: false
  },
  returnNull: false
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLocale;
}

export function getLocale(): Locale {
  const lng = (i18n.language ?? DEFAULT_LOCALE) as string;
  return lng.startsWith('da') ? 'da' : 'en';
}

export function getIntlLocale(): string {
  return INTL_TAG[getLocale()];
}

export function setLocale(next: Locale): void {
  if (!SUPPORTED_LOCALES.includes(next)) return;
  void i18n.changeLanguage(next);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, next);
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = next;
  }
}

export default i18n;
